import { useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import type { RangeOption } from "../../../constants/epicLeaderboard";
import type { PkOpponentRow, PkOpponents } from "../../../types/playerPk";
import { formatEtDateTime } from "../../../utils/etDate";
import PlayerLink from "../../common/PlayerLink";
import HelpTip from "../../common/HelpTip";
import KillLists from "./KillLists";
import "../../epicLeaderboard/epicLeaderboard.css";
import "./pk.css";

type SortKey = "opponent" | "kills" | "deaths" | "net" | "assistsOn" | "lastFightEt";

const COLUMNS: {
  key: SortKey;
  label: string;
  numeric: boolean;
  tooltip: string;
}[] = [
  {
    key: "opponent",
    label: "Opponent",
    numeric: false,
    tooltip: "Everyone this player killed, died to, or assisted a kill on."
  },
  {
    key: "kills",
    label: "Kills",
    numeric: true,
    tooltip: "Times this player killed the opponent."
  },
  {
    key: "deaths",
    label: "Deaths",
    numeric: true,
    tooltip: "Times the opponent killed this player (as the killer, not an assister)."
  },
  {
    key: "net",
    label: "Net",
    numeric: true,
    tooltip:
      "Kills minus deaths against this opponent. Positive means this player is ahead. Assists don't count."
  },
  {
    key: "assistsOn",
    label: "Assists on",
    numeric: true,
    tooltip:
      "Kills on the opponent where this player assisted rather than made the kill."
  },
  {
    key: "lastFightEt",
    label: "Last fight",
    numeric: false,
    tooltip: "Most recent kill, death or assist involving both (Eastern Time)."
  }
];

const COLLAPSED_ROWS = 15;

const compare = (a: PkOpponentRow, b: PkOpponentRow, key: SortKey): number => {
  if (key === "opponent") {
    return a.opponent.localeCompare(b.opponent, undefined, {
      sensitivity: "base"
    });
  }
  // ET strings sort chronologically as plain text.
  if (key === "lastFightEt") return a.lastFightEt.localeCompare(b.lastFightEt);
  return a[key] - b[key];
};

type Props = {
  opponents: PkOpponents;
  range: RangeOption;
  onCompare: (opponent: string) => void;
};

const OpponentsTable = ({ opponents, range, onCompare }: Props) => {
  // null keeps the API's order (most fights first).
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 } | null>(null);
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(() => {
    if (!sort) return opponents.rows;
    return [...opponents.rows].sort(
      (a, b) => compare(a, b, sort.key) * sort.dir
    );
  }, [opponents.rows, sort]);

  const visible = expanded ? rows : rows.slice(0, COLLAPSED_ROWS);

  const toggleSort = (key: SortKey) =>
    setSort((prev) => {
      if (!prev || prev.key !== key) {
        return { key, dir: key === "opponent" ? 1 : -1 };
      }
      return prev.dir === -1 ? { key, dir: 1 } : null;
    });

  const link = (name: string) => (
    <PlayerLink className="pk-link" player={name} range={range} tab="pk" />
  );

  return (
    <>
      <KillLists opponents={opponents} range={range} />

      {opponents.rows.length === 0 ? (
        <div className="pk-card text-center">No opponents in this range.</div>
      ) : (
        <>
          <div className="epic-lb-table-scroll">
            <table className="epic-lb-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={col.numeric ? "pk-num" : undefined}
                      aria-sort={
                        sort?.key === col.key
                          ? sort.dir === 1
                            ? "ascending"
                            : "descending"
                          : undefined
                      }
                    >
                      <button
                        type="button"
                        className="pk-sort-btn"
                        onClick={() => toggleSort(col.key)}
                      >
                        {col.label}
                        {sort?.key === col.key &&
                          (sort.dir === 1 ? " ▲" : " ▼")}
                      </button>
                      <HelpTip text={col.tooltip} />
                    </th>
                  ))}
                  <th aria-label="Compare" />
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.opponent}>
                    <td>{link(row.opponent)}</td>
                    <td className="pk-num">{row.kills}</td>
                    <td className="pk-num">{row.deaths}</td>
                    <td
                      className={`pk-num ${
                        row.net > 0 ? "pk-pos" : row.net < 0 ? "pk-neg" : ""
                      }`}
                    >
                      {row.net > 0 ? `+${row.net}` : row.net}
                    </td>
                    <td className="pk-num">{row.assistsOn}</td>
                    <td className="epic-lb-muted" style={{ whiteSpace: "nowrap" }}>
                      {formatEtDateTime(row.lastFightEt)}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-info"
                        onClick={() => onCompare(row.opponent)}
                      >
                        Compare
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > COLLAPSED_ROWS && (
            <div className="text-center mt-3">
              <Button
                size="sm"
                variant="outline-light"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? "Show fewer" : `Show all ${rows.length}`}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default OpponentsTable;
